import type { Face } from '../mahjong-solitaire/tiles.js';
import {
  type Decomposition,
  type MahjongSet,
  decompositions,
  isBonusFace,
  isDragon,
  isHonour,
  suitOf,
} from './tiles.js';

export type MahjongWind = 'east' | 'south' | 'west' | 'north';

/** A meld on the table. A kong has four tiles and counts as a pung. `concealed` is only true for a kong declared from the hand. */
export interface MahjongMeld {
  readonly kind: 'chow' | 'pung' | 'kong';
  readonly tiles: readonly Face[];
  readonly concealed: boolean;
}

/**
 * Scoring, a deliberately small Hong Kong style table. A hand is worth "faan" (doubles), a hand needs at least MIN_FAAN to win, and faan stop
 * counting at FAAN_CAP (a "limit" hand). Faan become points through POINTS_BY_FAAN. Nobody ever loses points: the winner scores, everyone
 * else keeps what they had, so points are only a score (docs/proposals/web-only-games.md and mahjong.md).
 *
 * Features (the best way of splitting the hand is scored):
 *   Self-drawn win .......................... 1
 *   Concealed hand (self-drawn, nothing claimed from a discard; concealed kongs allowed) .. 1
 *   Common hand (four chows and a pair that is not honours) .. 1
 *   All pungs (four pungs or kongs) ......... 3
 *   Dragon pung (each of red, green, white) .. 1
 *   Seat wind pung / round wind pung ......... 1 each (both when they are the same wind)
 *   Small three dragons (two dragon pungs and a dragon pair) .. 3 more, so 5 with the two pungs
 *   Mixed one suit (one suit plus honours) .. 3
 *   Pure one suit (one suit and nothing else) .. 6
 *   Big three dragons / All honours ......... limit (the cap)
 *   Flowers (only when the flowers option is on) .. 1 each
 */
export const MIN_FAAN = 1;
export const FAAN_CAP = 8;
/** Points for 0..8 faan; a hand under MIN_FAAN cannot win, so index 0 is never paid. */
export const POINTS_BY_FAAN: readonly number[] = [0, 1, 2, 4, 6, 8, 12, 16, 24];

export interface ScoreFeature {
  readonly name: string;
  readonly faan: number;
}

export interface MahjongScore {
  /** Faan counted, at most FAAN_CAP. */
  readonly faan: number;
  /** Faan before the cap. */
  readonly raw: number;
  /** True for a limit hand (the cap was reached or a limit feature applies). */
  readonly limit: boolean;
  readonly points: number;
  readonly features: readonly ScoreFeature[];
}

export interface ScoreContext {
  readonly selfDrawn: boolean;
  readonly seatWind: MahjongWind;
  readonly roundWind: MahjongWind;
  /** The melds already on the table for this seat. */
  readonly melds: readonly MahjongMeld[];
  /** Flowers and seasons set aside by this seat (0 unless the flowers option is on). */
  readonly bonus: number;
}

const asSet = (m: MahjongMeld): MahjongSet => ({ kind: m.kind === 'chow' ? 'chow' : 'pung', tiles: m.tiles.slice(0, 3) });

function scoreSplit(d: Decomposition, ctx: ScoreContext): MahjongScore {
  const sets: MahjongSet[] = [...ctx.melds.map(asSet), ...d.sets];
  const features: ScoreFeature[] = [];
  let limit = false;
  const add = (name: string, faan: number) => features.push({ name, faan });
  const pungs = sets.filter((s) => s.kind === 'pung');

  if (ctx.selfDrawn) add('Self-drawn win', 1);
  if (ctx.selfDrawn && ctx.melds.every((m) => m.concealed)) add('Concealed hand', 1);
  if (pungs.length === 0 && !isHonour(d.pair)) add('Common hand', 1);
  if (pungs.length === 4) add('All pungs', 3);

  const dragonPungs = pungs.filter((s) => isDragon(s.tiles[0] as Face));
  for (const s of dragonPungs) add(`Dragon pung (${(s.tiles[0] as Face).split('-')[1]})`, 1);
  for (const s of pungs) {
    const f = s.tiles[0] as Face;
    if (f === `wind-${ctx.seatWind}`) add('Seat wind pung', 1);
    if (f === `wind-${ctx.roundWind}`) add('Round wind pung', 1);
  }
  if (dragonPungs.length === 2 && isDragon(d.pair)) add('Small three dragons', 3);
  if (dragonPungs.length === 3) {
    add('Big three dragons', FAAN_CAP);
    limit = true;
  }

  const all: Face[] = [...sets.flatMap((s) => s.tiles), d.pair, d.pair];
  if (all.every(isHonour)) {
    add('All honours', FAAN_CAP);
    limit = true;
  } else {
    const suits = new Set(all.map(suitOf).filter((s) => s !== null));
    if (suits.size === 1) add(all.some(isHonour) ? 'Mixed one suit' : 'Pure one suit', all.some(isHonour) ? 3 : 6);
  }
  if (ctx.bonus > 0) add(ctx.bonus === 1 ? 'Flower' : 'Flowers', ctx.bonus);

  const raw = features.reduce((a, f) => a + f.faan, 0);
  const faan = Math.min(raw, FAAN_CAP);
  return { faan, raw, limit: limit || raw >= FAAN_CAP, points: POINTS_BY_FAAN[faan] as number, features };
}

/**
 * Score a complete hand: the concealed tiles plus the melds in `ctx`. Null when the concealed tiles are not complete (four sets in all and
 * a pair). Does not apply MIN_FAAN: callers compare `faan` with it, because a complete hand under the minimum may not win.
 */
export function scoreHand(concealed: readonly Face[], ctx: ScoreContext): MahjongScore | null {
  if (concealed.some(isBonusFace)) return null;
  const splits = decompositions(concealed, 4 - ctx.melds.length);
  let best: MahjongScore | null = null;
  for (const d of splits) {
    const s = scoreSplit(d, ctx);
    if (!best || s.raw > best.raw) best = s;
  }
  return best;
}
